import { memo, useEffect, useRef, useState } from 'react'

import { Button, Text } from '@cash-me/react-components'
import Image from 'next/image'

import { Col, Grid } from 'components/grid'
import { Polygon } from 'components/polygon'
import useWindowSize from 'hooks/useWindowSize'
import { theme } from 'styles/theme'

import { condominiumCreditBenefits } from './data'
import * as S from './styles'

type Props = {
  onCtaClick: () => void
}

export const CondominiumCreditLifeProject = memo(({ onCtaClick }: Props) => {
  const { isTablet } = useWindowSize()
  const sectionRef = useRef<HTMLElement>(null)
  const [hasAnimated, setHasAnimated] = useState(false)

  useEffect(() => {
    const element = sectionRef.current

    if (!element || hasAnimated) return

    // Os cards partem de opacity: 0, então sem o observer o conteúdo ficaria
    // invisível para sempre — melhor mostrar tudo do que esconder.
    if (typeof IntersectionObserver === 'undefined') {
      setHasAnimated(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Trava depois do primeiro disparo: a entrada acontece uma vez só,
        // e não deve ser desfeita ao rolar para longe da seção.
        if (entry.isIntersecting) setHasAnimated(true)
      },
      // Threshold baixo de propósito: a seção é mais alta que a viewport na
      // maioria das telas, então exigir muita área visível nunca dispararia.
      { threshold: 0.2 }
    )

    observer.observe(element)

    return () => observer.unobserve(element)
  }, [hasAnimated])

  const animationClass = hasAnimated ? 'animate' : ''

  return (
    <S.Section
      $bg="contrast"
      ref={sectionRef}
      className={animationClass}
      aria-labelledby="condominium-life-project-title"
    >
      <Grid col={{ phone: 1, tablet: 2 }}>
        <Col col={{ tablet: 1, desktop: 4 }} start={{ desktop: 2 }}>
          <S.ImageWrapper className={animationClass}>
            <Image
              src={`${process.env.NEXT_PUBLIC_SITE_IMAGES_PREFIX}/images/site/credito-para-condominio/energia-solar-${isTablet ? 'desktop' : 'mobile'}.webp`}
              alt="Placas de energia solar cobrindo o estacionamento de um condomínio"
              width={isTablet ? 1600 : 1020}
              height={isTablet ? 510 : 800}
              sizes="100vw"
              quality={100}
              style={{ width: '100%', height: 'auto' }}
            />
            {!isTablet && (
              <Polygon
                aria-hidden
                type="TriangleRound"
                width={80}
                height={100}
                draw="fill"
                color={theme.colors['cashme-magenta']}
                className="background-polygon polygon-pink"
              />
            )}
          </S.ImageWrapper>
        </Col>

        <Col col={{ tablet: 1, desktop: 4 }} start={{ desktop: 8 }}>
          {isTablet && (
            <Polygon
              aria-hidden
              type="TriangleRound"
              width={80}
              height={100}
              draw="fill"
              color={theme.colors['cashme-magenta']}
              className="background-polygon polygon-pink"
            />
          )}

          <Text
            id="condominium-life-project-title"
            as="h2"
            size="heading2"
            weight="medium"
            color="var(--colors-neutral-text-contrast-default)"
          >
            Por que avaliar o{' '}
            <Text as="span" size="heading2" weight="medium" color="var(--colors-cyan-background-pressed)">
              Crédito para Condomínios?
            </Text>
          </Text>

          <S.BenefitsList className={animationClass}>
            {condominiumCreditBenefits.map(({ id, icon, title, description }) => (
              <S.BenefitItem key={id}>
                <div aria-hidden>{icon}</div>
                <Text as="h3" size="heading5" weight="medium" color="var(--colors-neutral-text-contrast-default)">
                  {title}
                </Text>
                <Text size="bodySmall" color="var(--colors-neutral-text-contrast-default)">
                  {description}
                </Text>
              </S.BenefitItem>
            ))}
          </S.BenefitsList>

          <Button
            aria-label="Solicitar uma simulação de crédito para condomínios"
            size="lg"
            type="button"
            variants="text-only"
            onClick={onCtaClick}
          >
            <span>Solicitar uma simulação</span>
          </Button>
        </Col>
      </Grid>
    </S.Section>
  )
})

CondominiumCreditLifeProject.displayName = 'CondominiumCreditLifeProject'
